import { TlfContext } from "contexts/TlfContext";
import { eTlfItemType } from "functions/api/enums";
import { isTlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { ReactNode, useContext } from "react";
import { Col, Row } from "react-bootstrap";
import { Footnotes } from "./Footnotes";
import { InteractiveFigure } from "./InteractiveFigure";
import { NotesTable } from "./NotesTable";
import { PageBy } from "./PageBy";
import { ProgrammerNotes } from "./ProgrammerNotes";
import { StandardsNotes } from "./StandardsNotes";
import { TableListing } from "./TableListing";
import { Titles } from "./Titles";
import { TlfArsListing } from "./TlfArsListing";
import { TlfArsTable } from "./TlfArsTable";
import { TlfUndoButtons } from "./TlfUndoButtons";
import { V1Figure } from "./V1Figure";

/**
 * Core TLF content renderer that shows titles, body (table/figure) and footnotes.
 *
 * @returns React node containing the main TLF content or null when no TLF loaded.
 */
export const TlfInner = (): ReactNode => {
  const tlfContext = useContext(TlfContext);
  const tlfData = tlfContext?.tlfData;

  return !isTlfItemReadDto(tlfData) ? null : (
    <>
      <Row className="mt-4">
        <Col className="d-flex justify-content-center">
          <Titles />
        </Col>
      </Row>
      <PageBy />
      <TlfUndoButtons />
      {tlfData.type === eTlfItemType.v1figure ? (
        <Row id="tlf-body-holder-row">
          <Col
            id="tlf-body-holder-col"
            className="small-scrollbar"
            style={{ display: "grid", placeItems: "center" }}
          >
            <V1Figure />
          </Col>
        </Row>
      ) : tlfData.type === eTlfItemType.figure ? (
        <Row
          id="tlf-body-holder-row"
          className="mb-3 w-100"
        >
          <Col
            id="tlf-body-holder-col"
            className="w-100"
          >
            <InteractiveFigure />
          </Col>
        </Row>
      ) : tlfData.type === eTlfItemType.arsListing ? (
        <Row id="tlf-body-holder-row">
          <Col
            id="tlf-body-holder-col"
            className="small-scrollbar position-relative"
            style={{ display: "grid", placeItems: "center" }}
          >
            <TlfArsListing />
          </Col>
        </Row>
      ) : tlfData.type === eTlfItemType.arsTable ? (
        <Row id="tlf-body-holder-row">
          <Col
            id="tlf-body-holder-col"
            className="small-scrollbar position-relative"
            style={{ display: "grid", placeItems: "center" }}
          >
            <TlfArsTable />
          </Col>
        </Row>
      ) : (
        <Row id="tlf-body-holder-row">
          <Col
            id="tlf-body-holder-col"
            className="small-scrollbar"
            style={{ display: "grid", placeItems: "center" }}
          >
            <TableListing />
          </Col>
        </Row>
      )}
      <Row className="my-2">
        <Col className="mt-4">
          <Footnotes canAddFirstRow={true} />
        </Col>
      </Row>
    </>
  );
};

TlfInner.displayName = "TlfInner";

/**
 * High-level TLF view wrapper that conditionally renders `TlfInner` and related panels.
 *
 * @returns JSX element for the TLF page or a placeholder when no TLF is loaded.
 */
export const Tlf = (): React.ReactElement => {
  const tlfContext = useContext(TlfContext);
  const tlfData = tlfContext?.tlfData;

  return !tlfData ? (
    <Row
      id="tlf-row"
      className="mt-4 flex-grow-1 small-scrollbar w-100"
    >
      <Col
        className="d-flex justify-content-center"
        style={{ position: "relative" }}
      >
        <p>No TLF loaded, go to File -&gt; Load TLF</p>
      </Col>
    </Row>
  ) : (
    <>
      <TlfInner />
      <ProgrammerNotes />
      <NotesTable />
      <StandardsNotes />
    </>
  );
};

Tlf.displayName = "Tlf";
